import type { ServiceLabels } from '@/lib/form-state/util'
import { resourceIcon } from '@/layouts/admin/segment-labels'
import {
    moneyOrDash,
    netRevenue,
    resolveCost,
    resolveProfit,
} from '@/lib/billing/financials'
import {
    MdMedicalServices,
    MdLocalHospital,
    MdBiotech,
    MdHotel,
    MdChat,
    MdAccountBalanceWallet,
    MdGroups,
    MdHandshake,
    MdCampaign,
    MdPeople,
} from 'react-icons/md'

export interface ColumnDef {
    key: string
    label: string
    render?: (value: any, row: any) => string
    align?: 'left' | 'right' | 'center'
}

export interface FilterDef {
    key: string
    label: string
    /**
     * `boolean` é o Todos/Sim/Não (três estados). `toggle` é o liga-desliga: ligado
     * filtra `true`, desligado **não filtra** — para a pergunta que só tem um recorte
     * útil ("somente cirurgias"), em que o "Não" nunca é pedido.
     */
    type: 'select' | 'boolean' | 'toggle'
    options?: { value: string; label: string }[]
    /**
     * Aceita mais de um valor (vira `{ in: [...] }` no backend). Serializado como
     * `"PENDING,OVERDUE"` na query string, o mesmo formato de `catalogIds`.
     *
     * Vale para os filtros em que a pergunta operacional é naturalmente plural —
     * "o que tenho a pagar" é pendente **e** vencido. Filtro de dois valores
     * (Sim/Não, A Receber/A Pagar) não ganha nada com isto: "Todos" já resolve.
     */
    multi?: boolean
}

export interface ReportConfig {
    label: string
    icon: any
    columns: ColumnDef[]
    extraFilters: FilterDef[]
    statusLabels: Record<string, string>
    statusColors: Record<string, string>
    dateField: string
    hasFinancial?: boolean
}

const formatDate = (value: any) => {
    if (!value) return '-'
    return new Date(value).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const formatDateShort = (value: any) => {
    if (!value) return '-'
    return new Date(value).toLocaleDateString('pt-BR')
}

/**
 * Bases de período por tipo — o seletor "Contar período por". A primeira é o padrão
 * (`created`), e o tipo que não aparece aqui só conta por data de lançamento.
 *
 * Espelha `DATE_BASIS_FIELDS` do backend (`reports.service.ts`); mudou lá, mude aqui.
 */
export type DateBasis =
    | 'created'
    | 'scheduled'
    | 'due'
    | 'admission'
    | 'paid'
    | 'approved'
    | 'sold'

export const DATE_BASIS_OPTIONS: Record<
    string,
    { value: DateBasis; label: string; hint?: string }[]
> = {
    consultations: [
        { value: 'created', label: 'Data de lançamento', hint: 'Quando a consulta foi cadastrada no sistema' },
        { value: 'scheduled', label: 'Data agendada', hint: 'Quando o atendimento ocorre' },
        { value: 'paid', label: 'Data de pagamento', hint: 'Quando a cobrança foi paga — só entram as pagas' },
    ],
    orders: [
        { value: 'created', label: 'Data de lançamento', hint: 'Quando o procedimento foi cadastrado no sistema' },
        { value: 'scheduled', label: 'Data agendada', hint: 'Quando o atendimento ocorre' },
        { value: 'sold', label: 'Data da venda', hint: 'Aprovação do orçamento, ou o lançamento quando o procedimento já nasceu agendado — a mesma do dashboard financeiro' },
        { value: 'approved', label: 'Data da aprovação', hint: 'Quando o orçamento foi aprovado — só entram os aprovados' },
        { value: 'paid', label: 'Data de pagamento', hint: 'Quando a cobrança foi paga — só entram as pagas' },
    ],
    exams: [
        { value: 'created', label: 'Data de lançamento', hint: 'Quando o exame foi cadastrado no sistema' },
        { value: 'scheduled', label: 'Data agendada', hint: 'Quando o atendimento ocorre' },
        { value: 'sold', label: 'Data da venda', hint: 'Aprovação do orçamento, ou o lançamento quando o exame já nasceu agendado — a mesma do dashboard financeiro' },
        { value: 'approved', label: 'Data da aprovação', hint: 'Quando o orçamento foi aprovado — só entram os aprovados' },
        { value: 'paid', label: 'Data de pagamento', hint: 'Quando a cobrança foi paga — só entram as pagas' },
    ],
    billings: [
        { value: 'created', label: 'Data de lançamento', hint: 'Quando a cobrança foi criada' },
        { value: 'due', label: 'Vencimento', hint: 'Regime de competência' },
        { value: 'paid', label: 'Data de pagamento', hint: 'Regime de caixa — só entram as pagas' },
    ],
    admissions: [
        { value: 'created', label: 'Data de lançamento', hint: 'Quando a internação foi cadastrada' },
        { value: 'admission', label: 'Data de internação', hint: 'Quando o paciente foi internado' },
    ],
}

/**
 * Atendimentos (consulta, exame, procedimento) ABREM com o mesmo recorte do dashboard
 * financeiro: contados pela data da VENDA e sem orçamento nem cancelado. Decisão do
 * owner, depois de agosto/2026 ter sido fechado em R$ 350 mil por relatório —
 * somando orçamentos, pela data de lançamento — contra R$ 539,7 mil vendidos.
 *
 * Os status ficam MARCADOS no filtro, à vista (não é um recorte escondido): quem quer
 * ver orçamentos marca "Orçamento". Mexe só no ponto de partida da tela; a API e as
 * automações salvas continuam com o significado de antes.
 */
export const SALE_REPORT_TYPES: string[] = ['consultations', 'exams', 'orders']
const NON_SALE_STATUSES = ['BUDGET', 'CANCELLED']

/**
 * Venda perdida fora do recorte de venda (decisão do owner em 19/10/2026): atendimento à
 * vista, sem convênio nem cirurgia, cuja única cobrança venceu, foi cancelada ou estornada
 * não é venda, e a tabela "O que foi vendido" já não a conta. A regra mora no backend
 * (`lostSaleWhere`); aqui é só o liga-desliga, que abre ligado.
 */
const EXCLUDE_LOST_SALES_FILTER: FilterDef = {
    key: 'excludeLostSales',
    label: 'Ocultar cobranças perdidas',
    type: 'toggle',
}

export function saleDefaultsPatch(
    reportType: string,
    config?: { statusLabels?: Record<string, string> },
): { status?: string[]; dateBasis?: DateBasis; excludeLostSales?: boolean } {
    if (!SALE_REPORT_TYPES.includes(reportType) || !config?.statusLabels) return {}
    return {
        status: Object.keys(config.statusLabels).filter(s => !NON_SALE_STATUSES.includes(s)),
        // Consulta não passa por aprovação: a venda dela é o lançamento.
        dateBasis: reportType === 'consultations' ? 'created' : 'sold',
        excludeLostSales: true,
    }
}

/** Base padrão do período. O backend usa a mesma. */
export const DEFAULT_DATE_BASIS: DateBasis = 'created'

/**
 * `YYYY-MM-DD` do dia **local**, para os filtros de período.
 *
 * Nunca use `toISOString().split('T')[0]` para isso: ele converte para UTC antes de cortar a
 * string, então depois das 21h em Brasília já devolve a data de amanhã — e o período nasce
 * deslocado de um dia.
 */
export function toLocalDateString(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export const formatCurrency = (centavos: any) => {
    if (!centavos && centavos !== 0) return '-'
    const value = Number(centavos)
    if (isNaN(value) || value === 0) return '-'
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value / 100)
}

/** Espelha `BILLING_PAYMENT_METHOD_LABELS` de `reports.constants.ts` (backend). */
export const BILLING_PAYMENT_METHOD_LABELS: Record<string, string> = {
    PIX: 'PIX',
    BOLETO: 'Boleto',
    CREDIT_CARD: 'Cartão de Crédito',
    DEBIT_CARD: 'Cartão de Débito',
    CASH: 'Dinheiro',
    BANK_TRANSFER: 'Transferência Bancária',
    CHECK: 'Cheque',
    // Estava faltando: o enum tem TERMINAL desde a feature de maquininha.
    TERMINAL: 'Maquininha',
    OTHER: 'Outro',
}

/** Exibido quando não há cobrança, meio de pagamento ou conta bancária registrados. */
const NOT_INFORMED = '—'

export const BILLING_KIND_LABELS: Record<string, string> = {
    RECEIVABLE: 'A Receber',
    PAYABLE: 'A Pagar',
}

/** Rótulos do enum `BillingStatus` (espelha `reports.constants.ts` do backend). */
export const BILLING_STATUS_LABELS: Record<string, string> = {
    PENDING: 'Pendente',
    PAID: 'Pago',
    OVERDUE: 'Vencido',
    CANCELLED: 'Cancelado',
    REFUNDED: 'Estornado',
    PARTIALLY_PAID: 'Parcialmente Pago',
    IN_DISPUTE: 'Em Disputa',
    WRITTEN_OFF: 'Baixado',
}

export const BILLING_STATUS_COLORS: Record<string, string> = {
    PENDING: 'badge-warning',
    PAID: 'badge-success',
    OVERDUE: 'badge-error',
    CANCELLED: 'badge-ghost',
    REFUNDED: 'badge-info',
    PARTIALLY_PAID: 'badge-secondary',
    IN_DISPUTE: 'badge-error',
    WRITTEN_OFF: 'badge-ghost',
}

/**
 * Status de EXIBIÇÃO do pagamento em partes (flex ou parcelas avulsas): a conta ainda
 * não fechou, mas há cobrança viva. Espelha `BILLING_STATUS_IN_PROGRESS` do backend
 * (`libs/common/financials/billing-payment-state.ts`).
 *
 * Não é valor do enum `BillingStatus`, e por isso fica fora de `BILLING_STATUS_LABELS`:
 * nenhum status do enum diz "aprovado, ainda não quitado" sem mentir — `PENDING` é de
 * UMA cobrança (outra do mesmo procedimento pode estar paga) e `PARTIALLY_PAID` afirma
 * que entrou dinheiro, o que não vale para o orçamento aprovado sem pagamento nenhum.
 */
export const BILLING_STATUS_IN_PROGRESS = 'IN_PROGRESS'
export const BILLING_STATUS_IN_PROGRESS_LABEL = 'Em andamento'

/** Valor especial: registros sem cobrança vinculada. */
export const BILLING_STATUS_NONE = 'NONE'
export const BILLING_STATUS_NONE_LABEL = 'Sem cobrança'

/** Opções do filtro "Status da Cobrança" (enum + "Sem cobrança"). */
const BILLING_STATUS_FILTER: FilterDef = {
    key: 'billingStatus',
    label: 'Status da Cobrança',
    type: 'select',
    multi: true,
    options: [
        ...Object.entries(BILLING_STATUS_LABELS).map(([value, label]) => ({
            value,
            label,
        })),
        { value: BILLING_STATUS_NONE, label: BILLING_STATUS_NONE_LABEL },
    ],
}

/**
 * O mesmo filtro, mais "Em andamento" — só para PROCEDIMENTOS.
 *
 * É o único tipo com pagamento em partes (flex e parcelas avulsas penduradas na
 * cobrança principal), e portanto o único em que o status do CONJUNTO difere do
 * status da cobrança. Oferecer a opção em consulta ou exame prometeria um recorte que
 * o backend não tem o que calcular.
 */
const BILLING_STATUS_FILTER_WITH_PROGRESS: FilterDef = {
    ...BILLING_STATUS_FILTER,
    options: [
        ...Object.entries(BILLING_STATUS_LABELS).map(([value, label]) => ({
            value,
            label,
        })),
        {
            value: BILLING_STATUS_IN_PROGRESS,
            label: BILLING_STATUS_IN_PROGRESS_LABEL,
        },
        { value: BILLING_STATUS_NONE, label: BILLING_STATUS_NONE_LABEL },
    ],
}

/** Opções do filtro "Meio de Pagamento" (enum `BillingPaymentMethod`). */
const BILLING_PAYMENT_METHOD_FILTER: FilterDef = {
    key: 'billingPaymentMethod',
    label: 'Meio de Pagamento',
    type: 'select',
    multi: true,
    options: Object.entries(BILLING_PAYMENT_METHOD_LABELS).map(([value, label]) => ({
        value,
        label,
    })),
}

/**
 * Coluna "Status Cobrança" para tipos com relação to-one `billing`.
 * Sem `render`: o badge é montado em `report-table.tsx` (lê `row.billing.status`).
 */
const billingStatusColumn = (): ColumnDef => ({
    key: 'billingStatus',
    label: 'Status Cobrança',
})

/**
 * Coluna "Status Cobrança" para tipos com relação to-many `billings` (negócios).
 * Um negócio pode ter cobranças em estados diferentes — mostra os status distintos.
 */
const billingsStatusColumn = (): ColumnDef => ({
    key: 'billingsStatus',
    label: 'Status Cobrança',
    render: (_, row) => {
        const statuses = [
            ...new Set(
                (row.billings || [])
                    .map((b: any) => b?.status)
                    .filter(Boolean) as string[]
            ),
        ]
        if (statuses.length === 0) return BILLING_STATUS_NONE_LABEL
        return statuses.map(s => BILLING_STATUS_LABELS[s] || s).join(', ')
    },
})

export const LEAD_SOURCE_LABELS: Record<string, string> = {
    WHATSAPP: 'WhatsApp',
    MANUAL: 'Manual',
    WEBSITE: 'Site',
    REFERRAL: 'Indicação',
    CAMPAIGN: 'Campanha',
    UTM: 'UTM',
    SOCIAL_MEDIA: 'Redes Sociais',
    PHONE: 'Telefone',
    EMAIL: 'E-mail',
    PARTNER: 'Parceiro',
    OTHER: 'Outro',
}

const leadSource = (v: any) => LEAD_SOURCE_LABELS[v] || v || '-'

export const GENDER_LABELS: Record<string, string> = {
    MALE: 'Masculino',
    FEMALE: 'Feminino',
    OTHER: 'Outro',
    PREFER_NOT_TO_SAY: 'Não informado',
}

const formatLocation = (_: any, row: any) => {
    if (row.city) return row.state ? `${row.city}/${row.state}` : row.city
    return row.state || '-'
}

const sumPaidBillings = (billings: any[]) =>
    (billings || []).reduce(
        (s: number, b: any) => s + (b.status === 'PAID' ? b.amount || 0 : 0),
        0
    )

// ============ VENDA / CUSTO / LUCRO ============
// A aritmética vive em `@/lib/billing/financials` (compartilhada com o detalhe de
// exame). Regra: nenhum valor sai do catálogo (editável) — só snapshot da venda.

/**
 * Venda bruta e desconto em centavos. `Exam`/`Order` guardam `totalPrice`;
 * `Consultation` não guarda preço — reconstrói do líquido e do desconto percentual.
 */
/**
 * Traduz o tipo de relatório na origem da fatia — `null` para os que não são
 * atendimento (a linha É a cobrança, e ali o total é o número certo).
 * Espelha `appointmentSourceOf` do backend.
 */
const sourceOf = (type: string, row: any) => {
    const mapa: Record<string, string> = {
        exams: 'EXAM',
        consultations: 'CONSULTATION',
        orders: 'PROCEDURE',
        teleconsultations: 'TELECONSULTATION',
    }
    const sourceType = mapa[type]
    // `procedureTotals`: receita e custo do procedimento inteiro, apurados pelo
    // backend — valem até sem cobrança (orçamento, importado do legado).
    return sourceType && row?.id != null
        ? {
              sourceType,
              sourceId: row.id as number,
              procedureTotals: row.procedureTotals ?? null,
          }
        : null
}

/**
 * Valor da linha: numa cobrança agrupada é a FATIA deste atendimento, não o total
 * da cobrança — que pertence também aos outros e faria o relatório multiplicar.
 */
const valorDaLinha = (type: string, row: any): number | null => {
    // Procedimento flex ou pago em partes: o valor é a venda do procedimento, não a
    // parcela da cobrança principal (ver `computeProcedureFinancials` no backend).
    if (type === 'orders' && row.procedureTotals?.grossRevenue != null) {
        return row.procedureTotals.grossRevenue
    }
    const shares = row.billing?.payoutShares
    const source = sourceOf(type, row)
    if (shares?.length > 1 && source) {
        const fatia = shares.find(
            (s: any) =>
                s.sourceType === source.sourceType && s.sourceId === source.sourceId
        )
        if (fatia?.amount != null) return fatia.amount
    }
    return row.billing?.amount ?? null
}

const resolveSale = (
    type: string,
    row: any
): { grossSale: number | null; discountAmount: number | null } => {
    // Cobrança agrupada: a venda desta linha é a fatia dela, não o total que o
    // paciente pagou pelos três atendimentos.
    const net = netRevenue(row.billing, sourceOf(type, row))

    if (type === 'exams' || type === 'orders') {
        const gross = row.totalPrice ?? null
        if (gross == null) return { grossSale: null, discountAmount: null }
        const finalPrice = row.finalPrice ?? net
        if (finalPrice == null) return { grossSale: gross, discountAmount: null }
        return { grossSale: gross, discountAmount: Math.max(0, gross - finalPrice) }
    }

    if (type === 'consultations') {
        if (net == null) return { grossSale: null, discountAmount: null }
        const pct = row.discount ?? 0
        if (pct <= 0 || pct >= 100) return { grossSale: net, discountAmount: 0 }
        const gross = Math.round(net / (1 - pct / 100))
        return { grossSale: gross, discountAmount: gross - net }
    }

    return { grossSale: null, discountAmount: null }
}

/**
 * Coluna Pagamento. `billingOf` extrai a cobrança da linha — na aba Lançamentos
 * a própria linha é a cobrança.
 *
 * Havia uma coluna "Banco" ao lado, removida em 2026-08: ela só era preenchida
 * quando a cobrança gravava `bankAccountId`, o que os fluxos de agendamento não
 * fazem — na prática era uma coluna de traços ocupando espaço na tela e no
 * arquivo.
 */
const paymentColumns = (
    billingOf: (row: any) => any = row => row.billing
): ColumnDef[] => [
    {
        key: 'paymentMethod',
        label: 'Pagamento',
        render: (_, row) => {
            const method = billingOf(row)?.paymentMethod
            if (!method) return NOT_INFORMED
            return BILLING_PAYMENT_METHOD_LABELS[method] || method
        },
    },
]

/** Colunas Venda + Desconto para um tipo com preço snapshotado. */
const saleColumns = (type: string): ColumnDef[] => [
    {
        key: 'grossSale',
        label: 'Venda',
        align: 'right',
        render: (_, row) => moneyOrDash(resolveSale(type, row).grossSale),
    },
    {
        key: 'discountAmount',
        label: 'Desconto',
        align: 'right',
        render: (_, row) => moneyOrDash(resolveSale(type, row).discountAmount),
    },
]

/**
 * Colunas Custo + Lucro. `billingOf` extrai a cobrança da linha — na aba
 * Lançamentos a própria linha é a cobrança.
 */
/**
 * `type` identifica de qual atendimento é a linha. Numa cobrança agrupada, sem
 * isso, cada uma das três linhas mostraria o custo dos três.
 */
const costColumns = (
    type?: string,
    billingOf: (row: any) => any = row => row.billing
): ColumnDef[] => [
    {
        key: 'cost',
        label: 'Custo',
        align: 'right',
        render: (_, row) =>
            moneyOrDash(
                resolveCost(billingOf(row), type ? sourceOf(type, row) : null)
            ),
    },
    {
        key: 'profit',
        label: 'Lucro',
        align: 'right',
        render: (_, row) =>
            moneyOrDash(
                resolveProfit(billingOf(row), type ? sourceOf(type, row) : null)
            ),
    },
]

export const REPORT_CONFIGS: Record<string, ReportConfig> = {
    consultations: {
        label: 'Consultas',
        icon: MdMedicalServices,
        dateField: 'scheduledAt',
        hasFinancial: true,
        columns: [
            { key: 'id', label: 'ID' },
            {
                key: 'client',
                label: 'Paciente',
                render: (_, row) => row.client?.name || '-',
            },
            {
                key: 'professional',
                label: 'Profissional',
                render: (_, row) => row.professional?.name || '-',
            },
            {
                key: 'scheduledAt',
                label: 'Data Agendada',
                render: v => formatDate(v),
            },
            {
                key: 'createdAt',
                label: 'Agendado em',
                render: v => formatDate(v),
            },
            {
                key: 'createdBy',
                label: 'Criado por',
                render: (_, row) => row.createdBy?.name || '-',
            },
            { key: 'status', label: 'Status' },
            {
                key: 'isReturn',
                label: 'Retorno',
                render: v => (v ? 'Sim' : 'Não'),
            },
            {
                key: 'partner',
                label: 'Parceiro',
                render: (_, row) => row.partner?.name || '-',
            },
            { key: 'diagnosis', label: 'Diagnóstico', render: v => v || '-' },
            billingStatusColumn(),
            ...paymentColumns(),
            ...saleColumns('consultations'),
            {
                key: 'finalPrice',
                label: 'Valor',
                align: 'right',
                // Consultation não tem campo de preço: a venda só existe via billing.
                render: (_, row) =>
                    moneyOrDash(valorDaLinha('consultations', row)),
            },
            ...costColumns('consultations'),
        ],
        extraFilters: [
            { key: 'isReturn', label: 'É retorno?', type: 'boolean' },
            BILLING_STATUS_FILTER,
            BILLING_PAYMENT_METHOD_FILTER,
            EXCLUDE_LOST_SALES_FILTER,
        ],
        statusLabels: {
            REQUESTED: 'Solicitada',
            SCHEDULED: 'Agendada',
            WAITING: 'Aguardando',
            IN_PROGRESS: 'Em Andamento',
            COMPLETED: 'Concluída',
            CANCELLED: 'Cancelada',
            NO_SHOW: 'Não Compareceu',
        },
        statusColors: {
            REQUESTED: 'badge-info',
            SCHEDULED: 'badge-info',
            WAITING: 'badge-warning',
            IN_PROGRESS: 'badge-secondary',
            COMPLETED: 'badge-success',
            CANCELLED: 'badge-error',
            NO_SHOW: 'badge-ghost',
        },
    },

    orders: {
        label: 'Procedimentos',
        icon: MdLocalHospital,
        dateField: 'scheduledAt',
        hasFinancial: true,
        columns: [
            { key: 'id', label: 'ID' },
            {
                key: 'client',
                label: 'Paciente',
                render: (_, row) => row.client?.name || '-',
            },
            {
                key: 'professional',
                label: 'Profissional',
                render: (_, row) => row.professional?.name || '-',
            },
            {
                key: 'catalog',
                label: 'Procedimento',
                render: (_, row) => row.catalog?.name || '-',
            },
            {
                key: 'scheduledAt',
                label: 'Data Agendada',
                render: v => formatDate(v),
            },
            {
                key: 'createdAt',
                label: 'Agendado em',
                render: v => formatDate(v),
            },
            {
                key: 'createdBy',
                label: 'Criado por',
                render: (_, row) => row.createdBy?.name || '-',
            },
            { key: 'status', label: 'Status' },
            {
                key: 'partner',
                label: 'Parceiro',
                render: (_, row) => row.partner?.name || '-',
            },
            billingStatusColumn(),
            ...paymentColumns(),
            ...saleColumns('orders'),
            {
                key: 'catalogPrice',
                label: 'Valor',
                align: 'right',
                // `row.catalog.price` não existe mais (preço migrou para
                // CatalogCompanyPrice) e leria o catálogo ao vivo. Só snapshot.
                render: (_, row) =>
                    moneyOrDash(
                        valorDaLinha('orders', row) ??
                        row.finalPrice ??
                        row.totalPrice ??
                        null
                    ),
            },
            ...costColumns('orders'),
        ],
        extraFilters: [
            BILLING_STATUS_FILTER_WITH_PROGRESS,
            BILLING_PAYMENT_METHOD_FILTER,
            // Por último de propósito: são liga-desliga, não campos a preencher.
            { key: 'isSurgical', label: 'Somente cirurgias', type: 'toggle' },
            EXCLUDE_LOST_SALES_FILTER,
        ],
        statusLabels: {
            BUDGET: 'Orçamento',
            SCHEDULED: 'Agendado',
            IN_PROGRESS: 'Em Andamento',
            COMPLETED: 'Concluído',
            CANCELLED: 'Cancelado',
            POSTPONED: 'Adiado',
        },
        statusColors: {
            BUDGET: 'badge-ghost',
            SCHEDULED: 'badge-info',
            IN_PROGRESS: 'badge-secondary',
            COMPLETED: 'badge-success',
            CANCELLED: 'badge-error',
            POSTPONED: 'badge-warning',
        },
    },

    exams: {
        label: 'Exames',
        icon: MdBiotech,
        dateField: 'scheduledAt',
        hasFinancial: true,
        columns: [
            { key: 'id', label: 'ID' },
            {
                key: 'client',
                label: 'Paciente',
                render: (_, row) => row.client?.name || '-',
            },
            {
                key: 'professional',
                label: 'Profissional',
                render: (_, row) => row.professional?.name || '-',
            },
            { key: 'title', label: 'Título', render: v => v || '-' },
            {
                key: 'scheduledAt',
                label: 'Data Agendada',
                render: v => formatDate(v),
            },
            {
                key: 'createdAt',
                label: 'Agendado em',
                render: v => formatDate(v),
            },
            {
                key: 'createdBy',
                label: 'Criado por',
                render: (_, row) => row.createdBy?.name || '-',
            },
            { key: 'status', label: 'Status' },
            {
                key: 'partner',
                label: 'Parceiro',
                render: (_, row) => row.partner?.name || '-',
            },
            billingStatusColumn(),
            ...paymentColumns(),
            ...saleColumns('exams'),
            {
                key: 'finalPrice',
                label: 'Valor',
                align: 'right',
                render: (_, row) =>
                    moneyOrDash(
                        valorDaLinha('exams', row) ??
                        row.finalPrice ??
                        row.totalPrice ??
                        null
                    ),
            },
            ...costColumns('exams'),
        ],
        extraFilters: [BILLING_STATUS_FILTER, BILLING_PAYMENT_METHOD_FILTER, EXCLUDE_LOST_SALES_FILTER],
        statusLabels: {
            BUDGET: 'Orçamento',
            REQUESTED: 'Solicitado',
            IN_PROGRESS: 'Em Andamento',
            COMPLETED: 'Concluído',
            CANCELLED: 'Cancelado',
        },
        statusColors: {
            BUDGET: 'badge-ghost',
            REQUESTED: 'badge-info',
            IN_PROGRESS: 'badge-secondary',
            COMPLETED: 'badge-success',
            CANCELLED: 'badge-error',
        },
    },

    admissions: {
        label: 'Internações',
        icon: MdHotel,
        dateField: 'admissionDate',
        columns: [
            { key: 'id', label: 'ID' },
            { key: 'admissionNumber', label: 'Nº Internação' },
            {
                key: 'client',
                label: 'Paciente',
                render: (_, row) => row.client?.name || '-',
            },
            {
                key: 'attendingPhysician',
                label: 'Médico Responsável',
                render: (_, row) => row.attendingPhysician?.name || '-',
            },
            {
                key: 'unit',
                label: 'Unidade',
                render: (_, row) => row.unit?.name || '-',
            },
            {
                key: 'admissionDate',
                label: 'Data Internação',
                render: v => formatDateShort(v),
            },
            { key: 'status', label: 'Status' },
            {
                key: 'mainDiagnosis',
                label: 'Diagnóstico',
                render: v => v || '-',
            },
        ],
        extraFilters: [],
        statusLabels: {
            ADM_ACTIVE: 'Ativa',
            ADM_TRANSFERRED: 'Transferida',
            ADM_DISCHARGED: 'Alta',
            ADM_DEATH: 'Óbito',
            ADM_ESCAPED: 'Evasão',
            ADM_CANCELLED: 'Cancelada',
        },
        statusColors: {
            ADM_ACTIVE: 'badge-secondary',
            ADM_TRANSFERRED: 'badge-info',
            ADM_DISCHARGED: 'badge-success',
            ADM_DEATH: 'badge-error',
            ADM_ESCAPED: 'badge-warning',
            ADM_CANCELLED: 'badge-ghost',
        },
    },


    chats: {
        label: 'Atendimentos',
        icon: MdChat,
        dateField: 'createdAt',
        columns: [
            { key: 'id', label: 'ID' },
            {
                key: 'client',
                label: 'Cliente',
                render: (_, row) => row.client?.name || row.name || '-',
            },
            { key: 'phoneNumber', label: 'Telefone' },
            { key: 'status', label: 'Status' },
            {
                key: 'createdAt',
                label: 'Data',
                render: v => formatDate(v),
            },
        ],
        extraFilters: [],
        statusLabels: {
            OPEN: 'Aberto',
            CLOSED: 'Fechado',
        },
        statusColors: {
            OPEN: 'badge-success',
            CLOSED: 'badge-ghost',
        },
    },

    billings: {
        label: 'Lançamentos',
        icon: MdAccountBalanceWallet,
        dateField: 'dueDate',
        hasFinancial: true,
        columns: [
            { key: 'id', label: 'ID' },
            {
                key: 'type',
                label: 'Tipo',
                render: v => BILLING_KIND_LABELS[v] || v || '-',
            },
            {
                key: 'counterparty',
                label: 'Contraparte',
                // A quem se paga / de quem se recebe. Um lançamento a pagar pode apontar
                // para fornecedor, parceiro, profissional (repasse), colaborador de RH
                // (folha) ou usuário do sistema (reembolso) — a ordem reflete o que é mais
                // específico quando mais de um vem preenchido.
                render: (_, row) => {
                    if (row.type === 'PAYABLE') {
                        return (
                            row.supplier?.name ||
                            row.supplierName ||
                            row.employee?.name ||
                            row.professional?.name ||
                            row.partner?.name ||
                            row.partner?.tradingName ||
                            row.user?.name ||
                            '-'
                        )
                    }
                    return (
                        row.client?.name ||
                        row.partner?.name ||
                        row.partner?.tradingName ||
                        '-'
                    )
                },
            },
            {
                key: 'description',
                label: 'Descrição',
                render: v => v || '-',
            },
            {
                key: 'amount',
                label: 'Valor',
                align: 'right',
                render: v => formatCurrency(v),
            },
            {
                key: 'paidAmount',
                label: 'Valor Pago',
                align: 'right',
                render: v => formatCurrency(v),
            },
            { key: 'status', label: 'Status' },
            {
                // Repasse que já entrou numa solicitação agrupada continua listado aqui,
                // em aberto — e é a explicação mais frequente para "a tela lista dois
                // lançamentos e o agrupamento diz que não há nenhum". Sem esta coluna,
                // nada na linha distinguia um repasse já comprometido de um livre.
                key: 'payoutBatchId',
                label: 'Lote',
                render: v => (v ? `#${v}` : '-'),
            },
            {
                key: 'paymentMethod',
                label: 'Método',
                render: v => BILLING_PAYMENT_METHOD_LABELS[v] || v || NOT_INFORMED,
            },
            ...costColumns(undefined, row => row),
            {
                key: 'dueDate',
                label: 'Vencimento',
                render: v => formatDateShort(v),
            },
            {
                key: 'paidAt',
                label: 'Pagamento',
                render: v => formatDate(v),
            },
        ],
        extraFilters: [
            {
                key: 'type',
                label: 'Tipo de lançamento',
                type: 'select',
                options: [
                    { value: 'RECEIVABLE', label: 'A Receber' },
                    { value: 'PAYABLE', label: 'A Pagar' },
                ],
            },
            {
                key: 'paymentMethod',
                label: 'Método de Pagamento',
                type: 'select',
                multi: true,
                options: [
                    { value: 'PIX', label: 'PIX' },
                    { value: 'BOLETO', label: 'Boleto' },
                    { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
                    { value: 'DEBIT_CARD', label: 'Cartão de Débito' },
                    { value: 'CASH', label: 'Dinheiro' },
                    { value: 'BANK_TRANSFER', label: 'Transferência Bancária' },
                    { value: 'CHECK', label: 'Cheque' },
                    { value: 'OTHER', label: 'Outro' },
                ],
            },
        ],
        statusLabels: {
            PENDING: 'Pendente',
            PAID: 'Pago',
            OVERDUE: 'Vencido',
            CANCELLED: 'Cancelado',
            REFUNDED: 'Estornado',
            PARTIALLY_PAID: 'Parcialmente Pago',
            IN_DISPUTE: 'Em Disputa',
            WRITTEN_OFF: 'Baixado',
        },
        statusColors: {
            PENDING: 'badge-warning',
            PAID: 'badge-success',
            OVERDUE: 'badge-error',
            CANCELLED: 'badge-ghost',
            REFUNDED: 'badge-info',
            PARTIALLY_PAID: 'badge-warning',
            IN_DISPUTE: 'badge-error',
            WRITTEN_OFF: 'badge-ghost',
        },
    },

    leads: {
        label: 'Leads',
        icon: MdGroups,
        dateField: 'createdAt',
        columns: [
            { key: 'id', label: 'ID' },
            { key: 'name', label: 'Nome', render: v => v || '-' },
            { key: 'phoneNumber', label: 'Telefone', render: v => v || '-' },
            { key: 'status', label: 'Status' },
            { key: 'source', label: 'Origem', render: v => leadSource(v) },
            { key: 'metaAdId', label: 'Anúncio', render: v => v || '-' },
            {
                key: 'metaCampaignId',
                label: 'Campanha Meta',
                render: v => v || '-',
            },
            { key: 'metaFormId', label: 'Formulário', render: v => v || '-' },
            { key: 'utmSource', label: 'UTM Source', render: v => v || '-' },
            {
                key: 'utmCampaign',
                label: 'UTM Campaign',
                render: v => v || '-',
            },
            {
                key: 'campaign',
                label: 'Campanha',
                render: (_, row) => row.campaign?.name || '-',
            },
            {
                key: 'convertedAt',
                label: 'Convertido em',
                render: v => formatDateShort(v),
            },
            {
                key: 'createdAt',
                label: 'Criado em',
                render: v => formatDate(v),
            },
        ],
        extraFilters: [
            {
                key: 'status',
                label: 'Status',
                type: 'select',
                options: [
                    { value: 'NEW', label: 'Novo' },
                    { value: 'CONTACTED', label: 'Contatado' },
                    { value: 'QUALIFIED', label: 'Qualificado' },
                    { value: 'IN_NEGOTIATION', label: 'Em Negociação' },
                    { value: 'CONVERTED', label: 'Convertido' },
                    { value: 'LOST', label: 'Perdido' },
                ],
            },
        ],
        statusLabels: {
            NEW: 'Novo',
            CONTACTED: 'Contatado',
            QUALIFIED: 'Qualificado',
            IN_NEGOTIATION: 'Em Negociação',
            CONVERTED: 'Convertido',
            LOST: 'Perdido',
        },
        statusColors: {
            NEW: 'badge-info',
            CONTACTED: 'badge-secondary',
            QUALIFIED: 'badge-warning',
            IN_NEGOTIATION: 'badge-warning',
            CONVERTED: 'badge-success',
            LOST: 'badge-error',
        },
    },

    deals: {
        label: 'Negócios',
        icon: MdHandshake,
        dateField: 'createdAt',
        hasFinancial: true,
        columns: [
            { key: 'id', label: 'ID' },
            { key: 'title', label: 'Título', render: v => v || '-' },
            { key: 'status', label: 'Status' },
            {
                key: 'totalValue',
                label: 'Valor',
                align: 'right',
                render: v => formatCurrency(v),
            },
            billingsStatusColumn(),
            {
                key: 'paidRevenue',
                label: 'Receita Paga',
                align: 'right',
                render: (_, row) =>
                    formatCurrency(sumPaidBillings(row.billings)),
            },
            {
                key: 'lead',
                label: 'Lead',
                render: (_, row) => row.lead?.name || '-',
            },
            {
                key: 'leadOrigin',
                label: 'Origem',
                render: (_, row) => leadSource(row.lead?.source),
            },
            {
                key: 'metaAdId',
                label: 'Anúncio',
                render: (_, row) => row.lead?.metaAdId || '-',
            },
            {
                key: 'metaCampaignId',
                label: 'Campanha Meta',
                render: (_, row) => row.lead?.metaCampaignId || '-',
            },
            {
                key: 'closedAt',
                label: 'Fechado em',
                render: v => formatDateShort(v),
            },
            {
                key: 'createdAt',
                label: 'Criado em',
                render: v => formatDate(v),
            },
        ],
        extraFilters: [
            {
                key: 'status',
                label: 'Status',
                type: 'select',
                options: [
                    { value: 'OPEN', label: 'Aberto' },
                    { value: 'WON', label: 'Ganho' },
                    { value: 'LOST', label: 'Perdido' },
                    { value: 'ABANDONED', label: 'Abandonado' },
                ],
            },
            BILLING_STATUS_FILTER,
            BILLING_PAYMENT_METHOD_FILTER,
        ],
        statusLabels: {
            OPEN: 'Aberto',
            WON: 'Ganho',
            LOST: 'Perdido',
            ABANDONED: 'Abandonado',
        },
        statusColors: {
            OPEN: 'badge-info',
            WON: 'badge-success',
            LOST: 'badge-error',
            ABANDONED: 'badge-ghost',
        },
    },

    'marketing-funnel': {
        label: 'Funil',
        icon: MdCampaign,
        dateField: 'createdAt',
        hasFinancial: true,
        columns: [
            { key: 'name', label: 'Lead', render: v => v || '-' },
            { key: 'status', label: 'Status' },
            { key: 'source', label: 'Origem', render: v => leadSource(v) },
            { key: 'metaAdId', label: 'Anúncio', render: v => v || '-' },
            {
                key: 'metaCampaignId',
                label: 'Campanha Meta',
                render: v => v || '-',
            },
            { key: 'utmSource', label: 'UTM Source', render: v => v || '-' },
            {
                key: 'dealsCount',
                label: 'Negócios',
                align: 'right',
                render: (_, row) => String((row.deals || []).length),
            },
            {
                key: 'wonCount',
                label: 'Ganhos',
                align: 'right',
                render: (_, row) =>
                    String(
                        (row.deals || []).filter((d: any) => d.status === 'WON')
                            .length
                    ),
            },
            {
                key: 'totalValue',
                label: 'Valor Total',
                align: 'right',
                render: (_, row) =>
                    formatCurrency(
                        (row.deals || []).reduce(
                            (s: number, d: any) => s + (d.totalValue || 0),
                            0
                        )
                    ),
            },
            {
                key: 'paidRevenue',
                label: 'Receita Paga',
                align: 'right',
                render: (_, row) =>
                    formatCurrency(
                        (row.deals || []).reduce(
                            (s: number, d: any) =>
                                s + sumPaidBillings(d.billings),
                            0
                        )
                    ),
            },
            {
                key: 'convertedAt',
                label: 'Convertido em',
                render: v => formatDateShort(v),
            },
            {
                key: 'createdAt',
                label: 'Criado em',
                render: v => formatDate(v),
            },
        ],
        extraFilters: [
            {
                key: 'status',
                label: 'Status do Lead',
                type: 'select',
                options: [
                    { value: 'NEW', label: 'Novo' },
                    { value: 'CONTACTED', label: 'Contatado' },
                    { value: 'QUALIFIED', label: 'Qualificado' },
                    { value: 'IN_NEGOTIATION', label: 'Em Negociação' },
                    { value: 'CONVERTED', label: 'Convertido' },
                    { value: 'LOST', label: 'Perdido' },
                ],
            },
            BILLING_STATUS_FILTER,
            BILLING_PAYMENT_METHOD_FILTER,
        ],
        statusLabels: {
            NEW: 'Novo',
            CONTACTED: 'Contatado',
            QUALIFIED: 'Qualificado',
            IN_NEGOTIATION: 'Em Negociação',
            CONVERTED: 'Convertido',
            LOST: 'Perdido',
        },
        statusColors: {
            NEW: 'badge-info',
            CONTACTED: 'badge-secondary',
            QUALIFIED: 'badge-warning',
            IN_NEGOTIATION: 'badge-warning',
            CONVERTED: 'badge-success',
            LOST: 'badge-error',
        },
    },

    clients: {
        label: 'Clientes',
        icon: MdPeople,
        // Cliente é org-scoped (sem status). dateField = data de cadastro.
        dateField: 'createdAt',
        columns: [
            { key: 'id', label: 'ID' },
            { key: 'name', label: 'Nome', render: v => v || '-' },
            { key: 'cpf', label: 'CPF', render: v => v || '-' },
            { key: 'email', label: 'E-mail', render: v => v || '-' },
            { key: 'whatsAppNumber', label: 'WhatsApp', render: v => v || '-' },
            {
                key: 'gender',
                label: 'Sexo',
                render: v => GENDER_LABELS[v] || '-',
            },
            {
                key: 'birthDate',
                label: 'Nascimento',
                render: v => formatDateShort(v),
            },
            { key: 'location', label: 'Cidade/UF', render: formatLocation },
            {
                key: 'totalSpent',
                label: 'Gasto',
                align: 'right',
                render: v => formatCurrency(v),
            },
            {
                key: 'partner',
                label: 'Parceiro',
                render: (_, row) => row.partner?.name || '-',
            },
            {
                key: 'professional',
                label: 'Profissional',
                render: (_, row) => row.professional?.name || '-',
            },
            { key: 'createdAt', label: 'Cadastro', render: v => formatDate(v) },
        ],
        extraFilters: [
            {
                key: 'gender',
                label: 'Sexo',
                type: 'select',
                options: [
                    { value: 'MALE', label: 'Masculino' },
                    { value: 'FEMALE', label: 'Feminino' },
                    { value: 'OTHER', label: 'Outro' },
                    { value: 'PREFER_NOT_TO_SAY', label: 'Não informado' },
                ],
            },
        ],
        // Cliente não tem status; statusLabels vazio oculta o filtro de Status.
        statusLabels: {},
        statusColors: {},
    },
}

/**
 * `REPORT_CONFIGS` no vocabulário do segmento: fora da saúde, Procedimento vira
 * Pedido e Consulta vira Avaliação. Só os três relatórios de atendimento mudam;
 * o restante do mapa é devolvido intacto. Os ícones dos três seguem a mesma regra
 * do menu (`resourceIcon`): fora da saúde, nada de hospital ou microscópio.
 */
export function getReportConfigs(
    labels: ServiceLabels,
    segment: string
): Record<string, ReportConfig> {
    const iconFor = (resource: string) =>
        resourceIcon(segment, 'bookings', resource, REPORT_CONFIGS[resource].icon)

    const renameCatalogColumn = (config: ReportConfig, label: string) => ({
        ...config,
        columns: config.columns.map(column =>
            column.key === 'catalog' ? { ...column, label } : column
        ),
    })

    return {
        ...REPORT_CONFIGS,
        consultations: {
            ...renameCatalogColumn(
                REPORT_CONFIGS.consultations,
                labels.Consultation
            ),
            label: labels.Consultations,
            icon: iconFor('consultations'),
        },
        orders: {
            ...renameCatalogColumn(REPORT_CONFIGS.orders, labels.Order),
            label: labels.Orders,
            icon: iconFor('orders'),
        },
        exams: {
            ...renameCatalogColumn(REPORT_CONFIGS.exams, labels.Exam),
            label: labels.Exams,
            icon: iconFor('exams'),
        },
    }
}

/**
 * Recurso do menu que libera cada relatório. Mesma regra do sidebar (plano, role,
 * segmento): quem não tem "Exames" no menu também não vê o relatório de exames.
 * Relatório fora deste mapa não depende de recurso.
 */
export const REPORT_RESOURCE: Record<string, { module: string; resource: string }> = {
    consultations: { module: 'bookings', resource: 'consultations' },
    exams: { module: 'bookings', resource: 'exams' },
    orders: { module: 'bookings', resource: 'orders' },
    chats: { module: 'atendimento', resource: 'channels' },
    billings: { module: 'financial', resource: 'billings' },
    clients: { module: 'person', resource: 'clients' },
    leads: { module: 'sales', resource: 'leads' },
    deals: { module: 'sales', resource: 'deals' },
}

export const ORDER_TYPE_LABELS: Record<string, string> = {
    ORDER: 'Pedido',
    SCHEDULE: 'Agendamento',
}

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
    PENDING: 'Pendente',
    PAID: 'Pago',
    REFUNDED: 'Reembolsado',
    REFUND_REQUESTED: 'Reembolso Solicitado',
    REFUND_APPROVED: 'Reembolso Aprovado',
    REFUND_REJECTED: 'Reembolso Rejeitado',
}

// ─── Produtividade: filtros comuns aos três tipos ────────────────────────────

/** Os tipos de atendimento que a aba de produtividade comissiona. */
export const PRODUCTIVITY_TYPES = ['consultations', 'exams', 'orders'] as const

export type ProductivityType = (typeof PRODUCTIVITY_TYPES)[number]

/**
 * Situações do atendimento, num vocabulário **comum aos três tipos**.
 *
 * Os enums divergem — `BUDGET` só existe em exame e procedimento, `NO_SHOW` e
 * `WAITING` só em consulta, `POSTPONED` só em procedimento, e "solicitado" é
 * `REQUESTED` nos dois primeiros. O filtro da aba oferece a união, e cada seção
 * recebe apenas os valores que o seu tipo entende (`productivityStatusFor`).
 *
 * Sem isto, o operador teria de repetir a mesma escolha em três caixas de filtro
 * diferentes — que era exatamente o que fazia a comissão somar bases distintas: os
 * filtros valiam só na primeira seção e as demais traziam tudo, cancelados inclusive.
 */
export const PRODUCTIVITY_STATUS_OPTIONS: {
    value: string
    label: string
    /** Valor equivalente em cada tipo. Ausente = aquele tipo não tem essa situação. */
    byType: Partial<Record<ProductivityType, string>>
}[] = [
    {
        value: 'BUDGET',
        label: 'Orçamento',
        byType: { exams: 'BUDGET', orders: 'BUDGET' },
    },
    {
        value: 'REQUESTED',
        label: 'Solicitado',
        byType: { consultations: 'REQUESTED', exams: 'REQUESTED' },
    },
    {
        value: 'SCHEDULED',
        label: 'Agendado',
        byType: { consultations: 'SCHEDULED', orders: 'SCHEDULED' },
    },
    {
        value: 'WAITING',
        label: 'Aguardando',
        byType: { consultations: 'WAITING' },
    },
    {
        value: 'IN_PROGRESS',
        label: 'Em andamento',
        byType: {
            consultations: 'IN_PROGRESS',
            exams: 'IN_PROGRESS',
            orders: 'IN_PROGRESS',
        },
    },
    {
        value: 'COMPLETED',
        label: 'Concluído',
        byType: {
            consultations: 'COMPLETED',
            exams: 'COMPLETED',
            orders: 'COMPLETED',
        },
    },
    {
        value: 'CANCELLED',
        label: 'Cancelado',
        byType: {
            consultations: 'CANCELLED',
            exams: 'CANCELLED',
            orders: 'CANCELLED',
        },
    },
    {
        value: 'NO_SHOW',
        label: 'Não compareceu',
        byType: { consultations: 'NO_SHOW' },
    },
    {
        value: 'POSTPONED',
        label: 'Adiado',
        byType: { orders: 'POSTPONED' },
    },
]

/**
 * Situações que contam como VENDA na produtividade: todas menos orçamento e cancelado.
 * É o recorte padrão da aba (e o de "Limpar Filtros"), o mesmo do dashboard financeiro:
 * orçamento não é venda e cancelado não aconteceu — comissão sobre eles sai errada.
 */
export const PRODUCTIVITY_SALE_STATUSES: string[] = PRODUCTIVITY_STATUS_OPTIONS.map(o => o.value).filter(
    v => v !== 'BUDGET' && v !== 'CANCELLED',
)

/**
 * Traduz a escolha comum de situações para os valores de UM tipo.
 *
 * Devolve `undefined` quando nada foi escolhido (não filtra) e um array **vazio**
 * quando a escolha não se aplica àquele tipo — por exemplo "Não compareceu" para
 * exames. Os dois casos são diferentes e não podem virar o mesmo: sem filtro traz
 * tudo, e é justamente o que não pode acontecer quando o operador pediu um recorte
 * que aquele tipo não tem.
 */
export function productivityStatusFor(
    type: ProductivityType,
    selected?: string[]
): string[] | undefined {
    if (!selected?.length) return undefined
    return PRODUCTIVITY_STATUS_OPTIONS.filter(
        option => selected.includes(option.value) && option.byType[type]
    ).map(option => option.byType[type] as string)
}
